import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setAccessToken, setOrganizationId } from '../lib/api';
import { initTelegram } from '../lib/telegram';
import { capabilitiesOf, type Capabilities } from '../lib/permissions';

interface OrganizationRef {
  readonly id: string;
  readonly name: string;
  readonly role: string;
}

interface PendingAccess {
  readonly organizationId: string;
  readonly organizationName: string;
  readonly status: string;
}

interface SessionState {
  readonly status: 'loading' | 'ready' | 'error';
  readonly error: string | null;
  readonly userId: string;
  readonly userName: string;
  readonly organizations: readonly OrganizationRef[];
  readonly activeOrganizationId: string | null;
  /** Право завести организацию выдаётся секретным ключом через бота. */
  readonly canCreateOrganization: boolean;
  /** Заполнено, когда доступа ещё нет: показываем статус вместо интерфейса. */
  readonly pendingAccess: PendingAccess | null;
  readonly availableOrganizations: ReadonlyArray<{ id: string; name: string }>;
  /** Роль в активной организации: по ней интерфейс скрывает лишнее. */
  readonly role: string | null;
  readonly isDeveloper: boolean;
  readonly isAdmin: boolean;
  /** Что роль может делать. Зеркалит проверки на сервере. */
  readonly can: Capabilities;
  selectOrganization(id: string): void;
  refresh(): Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

const STORAGE_KEY = 'rp:activeOrganizationId';

/**
 * Логин выполняется один раз при старте: initData → JWT. Выбранная организация
 * запоминается локально, чтобы приложение открывалось там, где его закрыли.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionState['status']>('loading');
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [organizations, setOrganizations] = useState<readonly OrganizationRef[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [canCreate, setCanCreate] = useState(false);
  const [pendingAccess, setPendingAccess] = useState<PendingAccess | null>(null);
  const [available, setAvailable] = useState<ReadonlyArray<{ id: string; name: string }>>([]);

  const applyActive = (id: string | null) => {
    setActiveId(id);
    setOrganizationId(id);
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  };

  const load = async () => {
    setStatus('loading');
    setError(null);
    try {
      const session = await api.login();
      setAccessToken(session.accessToken);
      setUserId(session.user.id);
      setUserName(session.user.name);
      setOrganizations(session.organizations);
      setCanCreate(session.user.canCreateOrganization);
      setPendingAccess(session.pendingAccess);
      setAvailable(session.availableOrganizations);

      const remembered = localStorage.getItem(STORAGE_KEY);
      const valid = session.organizations.some((org) => org.id === remembered);
      applyActive(valid ? remembered : (session.organizations[0]?.id ?? null));
      setStatus('ready');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось войти');
      setStatus('error');
    }
  };

  useEffect(() => {
    initTelegram();
    void load();
    // Логин выполняется один раз за сессию приложения.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const role = organizations.find((organization) => organization.id === activeId)?.role ?? null;

  const value = useMemo<SessionState>(
    () => ({
      status,
      error,
      userId,
      userName,
      organizations,
      activeOrganizationId: activeId,
      role,
      isDeveloper: role === 'DEVELOPER',
      isAdmin: role === 'DEVELOPER' || role === 'OWNER' || role === 'ADMIN',
      can: capabilitiesOf(role),
      canCreateOrganization: canCreate,
      pendingAccess,
      availableOrganizations: available,
      selectOrganization: applyActive,
      refresh: load,
    }),
    [status, error, userId, userName, organizations, activeId, role, canCreate, pendingAccess, available],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used within SessionProvider');
  return context;
}
